import type { WeaponCategory } from './weapons';

/**
 * Game palette — kept in sync with the "Palette" section of docs/04-assets.md.
 * All procedural asset generators and the UI take colors from here.
 */
export const COLORS = {
  background: 0x14101f,
  outline: 0x1d1430,

  // Ground themes
  groundNebulaPink: 0xc4608c,
  groundVoidPurple: 0x3d3354,
  groundForestTeal: 0x2e6b63,
  groundMagmaDark: 0x4a2330,
  groundAbyssBlue: 0x16344e,
  abyssGlow: 0x4ab8e8,
  lavaOrange: 0xff7a30,

  // Elemental boss accents (docs/02 Elementals — Void / Earth / Air)
  voidCore: 0x241038,
  voidGlow: 0xb07cff,
  terraStone: 0x9c7a52,
  terraCrack: 0xff8a3c,
  galeWind: 0xd6f2ff,
  galeCore: 0x8fd4e8,

  // Danger / enemy
  dangerOrange: 0xe8542f,
  dangerRed: 0xd92b2b,
  bulletCoreDark: 0x1a0f0f,
  // Enemy bullet identities — the DANGER language (docs/04 wave 4): hot
  // red/orange rims, dark cores, spikes/fins. Off the player cyan/white
  // family (E4) AND the loot palette (gold gems, emerald cores, blue rims):
  // no amber/yellow, blue or green on a hostile shot.
  enemyBolt: 0xff5a1f,
  enemyBoltTip: 0xffb08a,
  enemyWind: 0xff8a4c,
  enemyWindEdge: 0xff4d1f,
  // Void = black eclipse disc + hot corona (was amber — next to gem gold)
  enemyVoidCorona: 0xff6a2e,
  enemyVoidCore: 0x140a06,
  enemyStar: 0xff6b3d,
  // Shard = crimson splinter (was a blue faceted diamond — read as a gem)
  enemyShard: 0xff3352,
  // Spore = crimson-rose pod (was olive 0x8aa83a ≈ the player laser lime)
  enemySpore: 0xe0446a,
  enemySporeCore: 0x3a0c1e,
  /** Hot ember dot inside dark cores (never a white glint — that is loot) */
  enemyEmber: 0xff9a6a,
  // Fused shots (bombs/eggs): charcoal shell, orange fuse spark
  enemyBomb: 0x2b1a17,
  enemyFuse: 0xff7a2e,
  enemyFuseCore: 0xffd8bf,

  // Player
  playerGreen: 0x7ec850,
  playerCream: 0xf0ead6,
  playerYellow: 0xf5a623,
  playerCyan: 0x45d9c5,

  // HUD vitals (docs/12 E1): HP = charge blue, armor = yellow, supershot = green
  hudHp: 0x4aa3e8,
  hudArmor: 0xf5a623,
  hudSupershot: 0x7ec850,

  // UI
  uiAccent: 0x45d9c5,
  // Stat direction markers (docs/12 C2): trade-off badges + stats panel deltas
  statUp: 0x4ad07a,
  statDown: 0xe8544c,
  rarityCommon: 0x9aa0a6,
  rarityUncommon: 0x4ad07a,
  rarityRare: 0x4a90d9,
  rarityEpic: 0x9b59d0,
  rarityLegendary: 0xf5a623,
  rarityMythic: 0xd92b2b,

  // Loot (docs/04 danger ↔ loot): gem gold, CORE emerald. CORE was red —
  // the enemy family — so it moved; the `gemRed` key stays (economy/save id
  // 'red'), only the colour changed.
  gemYellow: 0xf5c542,
  gemRed: 0x2bd46e,
  /** Pickup sparkle — the crisp white 4-point star only loot may carry */
  lootSparkle: 0xffffff,

  // Card / stat-panel glyph families (docs/03 W4): tint follows the STAT a
  // card touches, not its kind. Kept clear of enemy orange/red, armor/gem
  // yellow, legendary gold and the cyan focus accent.
  statPower: 0xff6b8b,
  statTempo: 0x5cc8ff,
  statSuper: 0xe68cff,
  statDefense: 0x63e38c,
  statMobility: 0x8b93ff,
  statEconomy: 0xf2f4f8,

  // Weapon categories (docs/12 E4) — the ONE source for projectiles, mounts,
  // crates and HUD/shop chips. Picked to stay clear of armor/legendary amber,
  // the cyan selection accent, rarity blue/purple and the enemy orange/red.
  weaponBullet: 0xe6ebf2,
  weaponPlasma: 0xa66bff,
  weaponLaser: 0xb6f23a,
  weaponBomb: 0xff5cb8,

  // Player-caused blasts (AoE impacts, kills): cool cyan/white family so they
  // never read as enemy fire. Enemy/boss destruction keeps the warm palette.
  fxBlastRing: 0x6fe3ff,
  fxBlastCore: 0xbff4ff,
  fxBlastHot: 0xf4fdff,
  /** Thin ring flashed on the ship when it takes a hit (no opaque blob) */
  fxHurtRing: 0xff4040,

  // Floating damage numbers (docs/12 F1): normal soft white, crit DARK red
  dmgNormal: 0xf0ead6,
  dmgCrit: 0xa01515,
  dmgHurt: 0xff4040,
} as const;

/** Weapon category → colour, read by every category-tinted visual. */
export const WEAPON_CATEGORY_COLOR: Readonly<Record<WeaponCategory, number>> = {
  bullet: COLORS.weaponBullet,
  plasma: COLORS.weaponPlasma,
  laser: COLORS.weaponLaser,
  bomb: COLORS.weaponBomb,
};

/** Stat family of a card glyph — weapon categories use their own colour. */
export type StatFamily = 'power' | 'tempo' | 'super' | 'defense' | 'mobility' | 'economy';

export const STAT_FAMILY_COLOR: Readonly<Record<StatFamily, number>> = {
  power: COLORS.statPower,
  tempo: COLORS.statTempo,
  super: COLORS.statSuper,
  defense: COLORS.statDefense,
  mobility: COLORS.statMobility,
  economy: COLORS.statEconomy,
};
